// Clinical Notes Panel — Fase 6 · Prontuário rápido (Fev/2026).
// Aba integrada ao modal de editar cliente (junto com Documentos/Cérebro).
// Permite ao profissional escrever prontuário em texto livre + agendar
// follow-up automático via WhatsApp.

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Stethoscope, PaperPlaneTilt, Bell } from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso.slice(0, 10); }
}

export default function ClinicalNotesPanel({ customerId, customerName }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [followupAt, setFollowupAt] = useState("");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/customer-clinical-notes/${customerId}`);
      setNotes(data?.notes || []);
    } catch { /* silencioso */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [customerId]);

  const save = async () => {
    if (text.trim().length < 5) {
      toast.error("Anotação muito curta.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/customer-clinical-notes", {
        customer_id: customerId,
        text: text.trim(),
        followup_at: followupAt || null,
        label: label.trim() || null,
      });
      toast.success(followupAt
        ? `Anotação salva. Follow-up agendado pra ${fmtDate(followupAt)}.`
        : "Anotação salva no Cérebro do cliente.");
      setText(""); setFollowupAt(""); setLabel("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar.");
    } finally { setSaving(false); }
  };

  const firstName = (customerName || "este cliente").split(" ")[0];

  return (
    <section
      className="mt-6 pt-5 border-t border-slate-100"
      data-testid="customer-clinical-notes-panel"
    >
      {/* Header ─────────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-900 text-white">
          <Stethoscope size={16} weight="fill" />
        </span>
        <div>
          <div className="text-[13px] font-semibold text-slate-900">
            Anotação profissional
            {notes.length > 0 && (
              <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100">
                {notes.length}
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 leading-tight">
            Prontuário rápido do atendimento. Vai automático pro 🧠 Cérebro do {firstName}.
          </div>
        </div>
      </div>

      {/* Form nova anotação ─────────────────────────────── */}
      <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2.5">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          maxLength={8000}
          placeholder="Ex: Rex 5kg, gastroenterite. Metronidazol 250mg 2x/dia por 7 dias. Retornar em 10 dias se persistir."
          data-testid="clinical-note-text-input"
          className="w-full rounded-md px-3 py-2 text-[12.5px] bg-white text-slate-800 border border-slate-200 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10 resize-none"
        />
        <div className="grid grid-cols-2 gap-2.5">
          <label className="block">
            <span className="text-[10.5px] font-semibold text-slate-500 uppercase tracking-wide">
              Título (opcional)
            </span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Consulta veterinária"
              maxLength={120}
              data-testid="clinical-note-label-input"
              className="mt-1 w-full rounded-md px-2.5 py-1.5 text-[12px] bg-white border border-slate-200 focus:outline-none focus:border-slate-400"
            />
          </label>
          <label className="block">
            <span className="text-[10.5px] font-semibold text-slate-500 uppercase tracking-wide inline-flex items-center gap-1">
              <Bell size={10} weight="fill" /> Follow-up automático
            </span>
            <input
              type="date"
              value={followupAt}
              onChange={(e) => setFollowupAt(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
              data-testid="clinical-note-followup-input"
              className="mt-1 w-full rounded-md px-2.5 py-1.5 text-[12px] bg-white border border-slate-200 focus:outline-none focus:border-slate-400"
            />
          </label>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10.5px] text-slate-500">
            {followupAt
              ? `AgentUS mandará WhatsApp em ${fmtDate(followupAt)} perguntando como está.`
              : "Sem follow-up automático marcado."}
          </span>
          <button
            type="button"
            onClick={save}
            disabled={saving || text.trim().length < 5}
            data-testid="clinical-note-save"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <PaperPlaneTilt size={12} weight="bold" />
            {saving ? "Salvando…" : "Salvar anotação"}
          </button>
        </div>
      </div>

      {/* Lista de anotações anteriores ───────────────────── */}
      {loading ? (
        <div className="mt-3 text-[12px] text-slate-400" data-testid="clinical-notes-loading">
          Carregando anotações…
        </div>
      ) : notes.length === 0 ? (
        <div
          className="mt-3 text-[12px] text-slate-500 italic bg-slate-50 border border-slate-100 rounded-lg p-3"
          data-testid="clinical-notes-empty"
        >
          Nenhuma anotação profissional ainda. Escreva a primeira acima.
        </div>
      ) : (
        <ul className="mt-3 space-y-2" data-testid="clinical-notes-list">
          {notes.map((n) => (
            <li
              key={n.id}
              data-testid={`clinical-note-${n.id}`}
              className="rounded-lg border border-slate-200 bg-white p-3"
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="min-w-0">
                  <div className="text-[12.5px] font-semibold text-slate-900 truncate">
                    {n.label || "Anotação"}
                  </div>
                  <div className="text-[10.5px] text-slate-500">
                    {fmtDate(n.created_at)}
                    {n.followup_at && (
                      <span className="ml-1.5 inline-flex items-center gap-0.5 text-emerald-700">
                        <Bell size={9} weight="fill" /> follow-up {fmtDate(n.followup_at)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-[12.5px] text-slate-700 leading-snug whitespace-pre-wrap">
                {n.text}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
